import { navTourId } from "./navTourId";
import type { TourDefinition, TourStep } from "./types";

/**
 * The tour catalogue. Each entry documents one surface: the staff Leads/CRM +
 * 9-stage pipeline work, and the owner/tenant portal homes. Steps anchor to
 * `data-tour="<id>"` nodes; an anchor that isn't visible on the current screen
 * (missing, hidden, or off-canvas) falls back to a centred card, so a tour
 * never points at nothing.
 *
 * Tour ids are also the "seen" keys persisted in localStorage — never rename an
 * id without a migration, or a user re-sees a completed tour.
 */

export const PIPELINE_TOUR: TourDefinition = {
  id: "pipeline-9-stage-v1",
  titleAr: "خط الأنابيب من 9 مراحل",
  titleEn: "The 9-stage pipeline",
  steps: [
    {
      titleAr: "مرحباً بك في خط الأنابيب الجديد",
      titleEn: "Meet the new pipeline",
      bodyAr:
        "حوّلنا المهام إلى رحلة من 9 مراحل تمتلكها ثلاثة فرق: المبيعات، العمليات، والمالية. هيا نشرحها مرحلة بمرحلة.",
      bodyEn:
        "Tasks now flow through 9 stages owned by three teams — Sales, Operations, and Finance. Let's walk them one by one.",
      placement: "center",
    },
    {
      target: "pipeline-col-new_lead",
      titleAr: "المبيعات: عميل جديد ← تم التواصل",
      titleEn: "Sales: New Lead → Contacted",
      bodyAr:
        "تبدأ كل رحلة هنا. «عميل جديد» يُنشأ تلقائياً عند تسجيل مالك أو اهتمام من إعلان، ثم «تم التواصل» بعد تحديد طلبه.",
      bodyEn:
        "Every journey starts here. New Lead is created automatically when an owner signs up or an ad interest is recorded; Contacted follows once you've identified what they need.",
      placement: "bottom",
    },
    {
      target: "pipeline-col-new_task",
      titleAr: "العمليات: مهمة جديدة ← قيد التنفيذ ← مكتمل",
      titleEn: "Operations: New Task → In Progress → Completed",
      bodyAr:
        "تتسلّم العمليات هنا: المعاينة، الزيارة، الإشراف، وعقود إيجار/ريجا. هذه المراحل الثلاث هي مجال بطاقات الصيانة أيضاً.",
      bodyEn:
        "Operations take over: assessment, visit, supervision, and Ejar/Rega contracts. These three stages are also the only ones maintenance cards can occupy.",
      placement: "bottom",
    },
    {
      target: "pipeline-col-negotiation",
      titleAr: "المبيعات مرة أخرى: تفاوض ← تم التعاقد",
      titleEn: "Back to Sales: Negotiation → Contracted",
      bodyAr:
        "بعد اكتمال العمل، تعود البطاقة إلى المبيعات لتحديد السعر وتفعيل العقد. الانتقال من «مكتمل» إلى «تفاوض» هو تسليم بين الفرق.",
      bodyEn:
        "After the work is done the card returns to Sales to set pricing and activate the contract. Moving Completed → Negotiation is a team hand-off.",
      placement: "bottom",
    },
    {
      target: "pipeline-col-invoicing",
      titleAr: "المالية: الفوترة ← التحصيل",
      titleEn: "Finance: Invoicing → Collection",
      bodyAr: "تختتم المالية الرحلة: إرسال الفاتورة والتذكير بها، ثم تأكيد السداد.",
      bodyEn: "Finance closes the journey: send and chase the invoice, then confirm it's paid.",
      placement: "bottom",
    },
    {
      target: "board-type-filter",
      titleAr: "أنواع المهام — ماذا يعني كل نوع",
      titleEn: "Task types — what each one means",
      bodyAr:
        "لون وأيقونة كل بطاقة يحددان نوع العمل. أعمال العمليات: معاينة (تقييم العقار)، زيارة، إشراف، طلب تعاقد، وصيانة. واهتمامات المبيعات: مهتم بالإيجار، بشراء أرض، أو بشراء مشروع. استخدم هذا الفلتر لعرض نوع واحد فقط.",
      bodyEn:
        "Each card's icon + colour is its work type. Operations work: Assessment (property eval), Visit, Supervision, Contract request, and Maintenance. Sales interests: Interested — Renting, Land, or Project. Use this filter to show just one type.",
      placement: "bottom",
    },
    {
      target: "board-team-filter",
      titleAr: "التصفية حسب الفريق",
      titleEn: "Filter by team",
      bodyAr:
        "اعرض مراحل فريقك فقط (المبيعات / العمليات / المالية) أو اللوحة كاملة. الشارة على كل بطاقة تُظهر «دور من» الآن بعد كل تسليم.",
      bodyEn:
        "Show only your team's stages (Sales / Operations / Finance) or the whole board. The badge on each card shows whose turn it is after each hand-off.",
      placement: "bottom",
    },
    {
      target: "pipeline-board-filters",
      titleAr: "البطاقات المؤرشفة",
      titleEn: "Archived cards",
      bodyAr: "البطاقات الملغاة تخرج من اللوحة النشطة. فعّل هذا الزر لعرضها واستعادتها عند الحاجة.",
      bodyEn:
        "Cancelled cards leave the active board. Toggle this to view and restore them when needed.",
      placement: "bottom",
    },
  ],
};

export const ADDRESS_BOOK_TOUR: TourDefinition = {
  id: "leads-address-book-v1",
  titleAr: "دفتر العملاء",
  titleEn: "The leads address book",
  steps: [
    {
      target: "leads-filters",
      titleAr: "تصفية على مستوى العميل",
      titleEn: "Lead-level filters",
      bodyAr:
        "ابحث بالاسم أو الجوال، وصفِّ حسب المصدر. هذه الصفحة عن العملاء — تفاصيل كل اهتمام وحالته تظهر داخل صفحة العميل.",
      bodyEn:
        "Search by name or phone, and filter by source. This page is about leads — each interest's detail and status live inside the lead's own page.",
      placement: "bottom",
    },
    {
      target: "leads-activity",
      titleAr: "نشاط العميل",
      titleEn: "Lead activity",
      bodyAr:
        "افصل بين من سجّل اهتماماً فعلياً ومن أنشأ حساباً فقط دون طلب — حتى تركّز على الطلب الحقيقي.",
      bodyEn:
        "Separate leads who registered a real interest from those who only signed up with no request — so you focus on actual demand.",
      placement: "start",
    },
    {
      target: "leads-row",
      titleAr: "افتح التفاصيل",
      titleEn: "Open the details",
      bodyAr:
        "انقر أي صف لفتح الملف الكامل: بيانات التواصل، كل اهتمام مع حالته وملاحظاته، والمهمة المرتبطة على اللوحة.",
      bodyEn:
        "Click any row for the full profile: contact details, every interest with its status and notes, and the linked task on the board.",
      placement: "top",
    },
  ],
};

export const CREATE_TASK_TOUR: TourDefinition = {
  id: "create-task-lead-v1",
  titleAr: "إنشاء مهمة + التقاط عميل",
  titleEn: "Create task + capture a lead",
  steps: [
    {
      target: "create-task-type",
      titleAr: "نوع المهمة يحدد المسار",
      titleEn: "The task type drives the flow",
      bodyAr:
        "اختيار نوع «مهتم» (إيجار/أرض/مشروع) يحوّل النموذج إلى التقاط عميل واهتمام — تُشتق النية من النوع تلقائياً، دون اختيار منفصل.",
      bodyEn:
        "Picking an interest type (rent / land / project) turns the form into a lead + interest capture — the intent is derived from the type, no separate toggle.",
      placement: "bottom",
    },
    {
      target: "create-task-status",
      titleAr: "المراحل التسع متاحة هنا",
      titleEn: "All 9 stages live here",
      bodyAr:
        "تعرض قائمة الحالة الآن المراحل التسع كاملة بدل البكتات القديمة، لتضع المهمة في مكانها الصحيح من البداية.",
      bodyEn:
        "The status dropdown now lists all 9 pipeline stages instead of the old buckets, so you place the task in the right column from the start.",
      placement: "bottom",
    },
    {
      target: "create-task-lead-fields",
      titleAr: "تفاصيل العميل",
      titleEn: "Lead details",
      bodyAr:
        "أدخل اسم العميل ورقمه (يُتحقّق من صحته)، الاستخدام، المدينة، والميزانية. سيظهر العميل فوراً في دفتر العملاء وتُنشأ له مهمة مرتبطة.",
      bodyEn:
        "Enter the lead's name and phone (validated), use, city, and budget. The lead appears in the address book immediately and gets a linked task.",
      placement: "top",
    },
  ],
};

// ── Portal tours (owner + tenant) ────────────────────────────────────────────
// Most steps point at a sidebar link. On a phone the sidebar is folded behind
// the ☰ menu button (the closed Sheet isn't even mounted), so those anchors are
// missing and the runtime shows the step centred. The step title is the menu
// item's own label and the fallback hint says where to find it — we never open
// the menu on the user's behalf.

/** data-tour id of the owner dashboard's summary-card grid. */
export const OWNER_SUMMARY_CARDS_TARGET = "owner-summary-cards";
/** data-tour id of the tenant dashboard's summary-card grid. */
export const TENANT_SUMMARY_CARDS_TARGET = "tenant-summary-cards";

const MENU_HINT = {
  fallbackHintAr: "تجدها في القائمة ☰ أعلى الصفحة.",
  fallbackHintEn: "You'll find it in the ☰ menu at the top of the page.",
} as const;

/** A step anchored to a portal sidebar link (centred + hinted on mobile). */
function navStep(
  url: string,
  step: Omit<TourStep, "target" | "fallbackHintAr" | "fallbackHintEn">,
): TourStep {
  return { target: navTourId(url), placement: "end", ...MENU_HINT, ...step };
}

export const OWNER_PORTAL_TOUR: TourDefinition = {
  id: "owner-portal-v1",
  titleAr: "جولة بوابة المالك",
  titleEn: "Owner portal tour",
  steps: [
    {
      titleAr: "أهلاً بك في بوابة المالك",
      titleEn: "Welcome to the owner portal",
      bodyAr: "تابع عقاراتك ودخلك وطلباتك من مكان واحد.",
      bodyEn: "Follow your properties, income and requests from one place.",
      placement: "center",
    },
    {
      target: OWNER_SUMMARY_CARDS_TARGET,
      titleAr: "ملخّص دخلك",
      titleEn: "Your income at a glance",
      bodyAr: "الإيرادات والمصاريف وصافي الدخل للفترة التي تختارها، ونسبة الإشغال.",
      bodyEn: "Revenue, expenses and net income for the period you pick, plus your occupancy rate.",
      placement: "bottom",
    },
    navStep("/portal/owner/properties", {
      titleAr: "عقاراتي",
      titleEn: "My Properties",
      bodyAr: "عقاراتك ووحداتها، وحالة كل وحدة: مؤجرة أو شاغرة.",
      bodyEn: "Your properties and their units, and whether each unit is rented or vacant.",
    }),
    navStep("/portal/owner/reports", {
      titleAr: "المالية",
      titleEn: "Finance",
      bodyAr: "تقاريرك المالية شهراً بشهر، والفواتير المستحقة والمتأخرة على عقاراتك.",
      bodyEn: "Your monthly financial reports, and the due and overdue invoices on your properties.",
    }),
    navStep("/portal/owner/documents", {
      titleAr: "العقود والمستندات",
      titleEn: "Contracts & Documents",
      bodyAr: "عقود الإيجار والفواتير وسندات القبض والمصروفات في مكان واحد.",
      bodyEn: "Lease contracts, invoices, receipts and expenses in one place.",
    }),
    navStep("/portal/owner/maintenance", {
      titleAr: "الطلبات",
      titleEn: "Requests",
      bodyAr: "طلبات الصيانة وما يحتاج موافقتك، وطلباتك من دلّالي، وزيارات عقاراتك.",
      bodyEn: "Maintenance requests and approvals, your requests to Dallali, and visits to your properties.",
    }),
    navStep("/portal/owner/messages", {
      titleAr: "التواصل",
      titleEn: "Messages",
      bodyAr: "تواصل مع دلّالي عبر واتساب — ويمكنك إعادة الجولة من زر «؟».",
      bodyEn: "Chat with Dallali on WhatsApp — you can replay this tour from the « ? » button.",
    }),
  ],
};

export const TENANT_PORTAL_TOUR: TourDefinition = {
  id: "tenant-portal-v1",
  titleAr: "جولة بوابة المستأجر",
  titleEn: "Tenant portal tour",
  steps: [
    {
      titleAr: "أهلاً بك في بوابة المستأجر",
      titleEn: "Welcome to the tenant portal",
      bodyAr: "تابع عقدك ودفعاتك وطلبات الصيانة من مكان واحد.",
      bodyEn: "Follow your contract, payments and maintenance requests from one place.",
      placement: "center",
    },
    {
      target: TENANT_SUMMARY_CARDS_TARGET,
      titleAr: "ملخّصك",
      titleEn: "Your summary",
      bodyAr: "عقودك ودفعاتك المستحقة وطلبات الخدمة للفترة التي تختارها.",
      bodyEn: "Your contracts, due payments and service requests for the period you pick.",
      placement: "bottom",
    },
    navStep("/portal/tenant/payments", {
      titleAr: "المدفوعات",
      titleEn: "Payments",
      bodyAr: "دفعاتك وفواتيرك وحالة كل دفعة.",
      bodyEn: "Your payments, invoices and the status of each payment.",
    }),
    navStep("/portal/tenant/lease", {
      titleAr: "عقدي",
      titleEn: "My Contract",
      bodyAr: "تفاصيل عقدك وتواريخه.",
      bodyEn: "Your contract details and key dates.",
    }),
    navStep("/portal/tenant/maintenance", {
      titleAr: "الصيانة",
      titleEn: "Maintenance",
      bodyAr: "ارفع طلب صيانة وتابع حالته.",
      bodyEn: "Submit a maintenance request and track its status.",
    }),
    navStep("/portal/tenant/notifications", {
      titleAr: "الإشعارات",
      titleEn: "Notifications",
      bodyAr: "تنبيهات الدفعات والطلبات.",
      bodyEn: "Alerts about your payments and requests.",
    }),
    navStep("/portal/tenant/contact", {
      titleAr: "التواصل",
      titleEn: "Contact",
      bodyAr: "تواصل مع دلّالي عبر واتساب أو مع مدير عقارك — وأعد الجولة من زر «؟».",
      bodyEn:
        "Chat with Dallali on WhatsApp or reach your property manager — replay this tour from the « ? » button.",
    }),
  ],
};

/** Every tour, by id. The launcher and auto-start read from here. */
export const TOURS: Record<string, TourDefinition> = {
  [PIPELINE_TOUR.id]: PIPELINE_TOUR,
  [ADDRESS_BOOK_TOUR.id]: ADDRESS_BOOK_TOUR,
  [CREATE_TASK_TOUR.id]: CREATE_TASK_TOUR,
  [OWNER_PORTAL_TOUR.id]: OWNER_PORTAL_TOUR,
  [TENANT_PORTAL_TOUR.id]: TENANT_PORTAL_TOUR,
};
